import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { mainApiUrl, baseHeaders, storeId as storeIdConstant, userTimezone } from '@constants'
import { base62Encode } from '@src/utils'
import { prepareHeaders, transformResponse } from '@src/reduxjs'

const storesApi = createApi({
  reducerPath: 'storesApi',
  baseQuery: fetchBaseQuery({ baseUrl: mainApiUrl, prepareHeaders }),
  tagTypes: ['Stores', 'CurrentStore', 'Logs', 'Locals', 'Albums', 'Costs', 'PixExchangeRates', 'Stats', 'MercadoPagoSubscription'],
  endpoints: (builder) => ({
    get: builder.query({
      query: (data = {}) => {
        const params = {}

        if (data.page) {
          params.page = data.page
        }

        if (data.perPage) {
          params.per_page = data.perPage
        }

        return { url: 'stores', params }
      },
      providesTags: (results) => {
        if (!results) {
          return [{ type: 'Stores', id: 'LIST' }]
        }
        return []
      },
    }),
    getLocals: builder.query({
      query: ({ page, perPage, storeId } = {}) => {
        const params = {}

        if (page) {
          params.page = page
        }

        if (perPage) {
          params.per_page = perPage
        }

        return { url: `stores/${storeId || storeIdConstant}/locals`, params }
      },
      providesTags: ['Locals'],
    }),
    getStats: builder.query({
      query: ({ from, to, storeId }) => {
        const params = { timezone: base62Encode(userTimezone) }

        if (from) {
          params.from = from
        }

        if (to) {
          params.to = to
        }

        return { url: `stores/${storeId || storeIdConstant}/stats`, params, method: 'GET' }
      },
      providesTags: () => ['Stats'],
      transformResponse,
    }),
    getLogs: builder.query({
      query: ({ from, to, storeId, category }) => {
        const params = { timezone: base62Encode(userTimezone) }

        if (from) {
          params.from = from
        }

        if (to) {
          params.to = to
        }

        if (category) {
          params.category = category
        }

        return { url: `stores/${storeId}/logs`, params, method: 'GET' }
      },
      providesTags: ['Logs'],
    }),
    getCosts: builder.query({
      query: ({ storeId } = {}) => {
        return { url: `stores/${storeId || storeIdConstant}/costs`, method: 'GET' }
      },
      providesTags: ['Costs'],
    }),
    getMercadoPagoSubscription: builder.query({
      query: ({ storeId } = {}) => {
        return { url: `stores/${storeId || storeIdConstant}/mp_subscription`, method: 'GET' }
      },
      providesTags: ['MercadoPagoSubscription'],
    }),
    addPayment: builder.mutation({
      query: (data) => {
        return {
          url: `stores/${storeIdConstant}/payments`,
          method: 'POST',
          body: data,
        }
      },
      invalidatesTags: (results, error, { id }) => {
        return ['Costs']
      },
    }),
    getPixExchangeRates: builder.query({
      query: ({ storeId } = {}) => {
        return { url: `stores/${storeId || storeIdConstant}/pix/rates`, method: 'GET' }
      },
      providesTags: ['PixExchangeRates'],
    }),
    getCurrent: builder.query({
      query: (storeId = null) => ({ url: `stores/${storeId || storeIdConstant}`, method: 'GET' }),
      invalidatesTags: (results, error, { id }) => {
        return [{ type: 'Stores', id }]
      },
      providesTags: () => ['CurrentStore'],
      transformResponse,
    }),
    update: builder.mutation({
      query: ({ store }) => {
        return {
          url: `stores/${store.id}`,
          method: 'PUT',
          body: store,
        }
      },
      invalidatesTags: (results, error, { id }) => {
        return ['CurrentStore']
      },
    }),
    getMPOAuthUrl: builder.mutation({
      query: ({ store }) => {
        return {
          url: `stores/${store.id}/mp/oauth/uri`,
          method: 'GET',
        }
      },
    }),
  }),
})

export const { useGetQuery, useGetLogsQuery, useGetStatsQuery, useGetCostsQuery, useGetLocalsQuery, useGetCurrentQuery, useUpdateMutation, useGetMPOAuthUrlMutation, useAddPaymentMutation, useGetPixExchangeRatesQuery, useGetMercadoPagoSubscriptionQuery } = storesApi

export default storesApi
